import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Loading, ErrorState, EmptyState, Pill, DemoBadge, Panel,
  STATUS_TONE, fmtMoney, Metric,
} from "../../components/app/Kit";

const STAGES = [
  ["new", "New"],
  ["contacted", "Contacted"],
  ["qualified", "Qualified"],
  ["booked", "Proposal / Booking"],
  ["won", "Won"],
  ["lost", "Lost"],
];

export function Pipeline() {
  const { data, state, error, reload, setData } = useResource("/leads");
  const [movingId, setMovingId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const move = async (lead, status) => {
    if (!lead || lead.status === status) return;
    setMovingId(lead.id);
    // Optimistic update
    const prev = data;
    setData?.(data.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      toast.success(`${lead.name} → ${STAGES.find(([k]) => k === status)?.[1] || status}`);
    } catch (e) {
      toast.error(apiError(e));
      setData?.(prev);
    } finally {
      setMovingId(null);
      reload();
    }
  };

  const onDragStart = (e, lead) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/lead-id", lead.id);
    e.dataTransfer.setData("text/lead-status", lead.status);
  };
  const onDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== key) setDragOver(key);
  };
  const onDrop = (e, status) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/lead-id");
    const from = e.dataTransfer.getData("text/lead-status");
    if (!id || from === status) return;
    const lead = data.find((l) => l.id === id);
    if (lead) move(lead, status);
  };

  if (state === "loading") return <Loading rows={8} testId="pipeline-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={reload} testId="pipeline-error" />;

  return (
    <div data-testid="pipeline-page">
      <PageTitle
        testId="pipeline-title"
        title="Pipeline"
        sub="Drag any lead card between stages — the CRM record updates the moment you drop it."
        action={<DemoBadge />}
      />
      {data.length === 0 ? (
        <EmptyState icon="GitBranch" title="No leads in the pipeline" sub="Your first lead will appear here." testId="pipeline-empty" />
      ) : (
        <div className="scrollbar-thin overflow-x-auto pb-3">
          <div className="flex min-w-max gap-3">
            {STAGES.map(([key, label]) => {
              const items = data.filter((l) => l.status === key);
              const active = dragOver === key;
              return (
                <div
                  key={key}
                  data-testid={`pipeline-column-${key}`}
                  onDragOver={(e) => onDragOver(e, key)}
                  onDragLeave={() => setDragOver((cur) => (cur === key ? null : cur))}
                  onDrop={(e) => onDrop(e, key)}
                  className={`w-[260px] shrink-0 rounded-lg border p-1 transition-colors ${
                    active ? "border-elv-green/50 bg-elv-green/[0.04]" : "border-transparent"
                  }`}
                >
                  <div className="glass flex items-center justify-between rounded-md px-3 py-2.5">
                    <span className="text-[11px] uppercase tracking-[0.16em] text-elv-text">{label}</span>
                    <span className="font-mono text-[11px] text-elv-green">{items.length}</span>
                  </div>
                  <p className="mt-2 px-1 text-[11px] text-elv-dim">
                    {fmtMoney(items.reduce((s, i) => s + (i.value || 0), 0))} pipeline value
                  </p>
                  <div className="mt-2 grid gap-2 min-h-[80px]">
                    {items.map((l) => (
                      <div
                        key={l.id}
                        data-testid={`pipeline-card-${l.id}`}
                        draggable
                        onDragStart={(e) => onDragStart(e, l)}
                        className={`glass glass-hover rounded-md p-3 cursor-grab active:cursor-grabbing ${
                          movingId === l.id ? "opacity-60" : ""
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <Link to={`/app/customers/${l.customer_id}`} className="text-xs font-medium text-elv-text hover:text-elv-green">
                            {l.name}
                          </Link>
                          <Icons.GripVertical className="h-3 w-3 shrink-0 text-elv-dim" />
                        </div>
                        <p className="mt-1.5 text-[11px] text-elv-dim">
                          {l.source} · {fmtMoney(l.value)}
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <Pill tone={STATUS_TONE[l.temperature]}>{l.temperature}</Pill>
                          <span className="font-mono text-[10px] text-elv-dim">{l.score}</span>
                        </div>
                        <select
                          value={l.status}
                          disabled={movingId === l.id}
                          onChange={(e) => move(l, e.target.value)}
                          data-testid={`pipeline-move-${l.id}`}
                          aria-label="Move to stage (keyboard alternative)"
                          className="mt-3 w-full rounded-md border border-elv-line bg-[#0A0F14] px-2 py-1.5 text-[11px] text-elv-text outline-none"
                        >
                          {STAGES.map(([s, sl]) => (
                            <option key={s} value={s}>{`Move to ${sl}`}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                    {items.length === 0 ? (
                      <p className="rounded-md border border-dashed border-white/[0.08] p-4 text-center text-[11px] text-elv-dim">
                        Drop leads here
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function CRM() {
  const analytics = useResource("/analytics/summary");
  const leads = useResource("/leads");
  const recent = useMemo(() => (leads.data || []).slice(0, 8), [leads.data]);

  if (analytics.state === "loading") return <Loading rows={8} testId="crm-loading" />;
  if (analytics.state === "error")
    return <ErrorState message={analytics.error} onRetry={analytics.reload} testId="crm-error" />;

  const p = analytics.data.pipeline;

  return (
    <div data-testid="crm-page">
      <PageTitle
        testId="crm-title"
        title="CRM"
        sub="Every lead. Every customer. One source of truth."
        action={<DemoBadge />}
      />

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {p.map((s) => (
          <Metric key={s.stage} testId={`crm-metric-${s.stage}`} label={s.stage} value={s.count} hint={fmtMoney(s.value)} tone={s.stage === "lost" ? "cyan" : "green"} />
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.3fr_1fr]">
        <Panel
          testId="crm-recent-leads"
          title="Recent leads"
          action={
            <Link to="/app/leads" data-testid="crm-all-leads" className="text-[11px] text-elv-green hover:text-elv-cyan">
              All leads
            </Link>
          }
        >
          {recent.length === 0 ? (
            <EmptyState icon="Target" title="No leads yet" sub="Your first lead will appear here." testId="crm-leads-empty" />
          ) : (
            <ul className="grid gap-2">
              {recent.map((l) => (
                <li key={l.id} data-testid={`crm-lead-${l.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                  <Link to={`/app/customers/${l.customer_id}`} className="text-xs font-medium text-elv-text hover:text-elv-green">
                    {l.name}
                  </Link>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-elv-dim">{l.source}</span>
                    <Pill tone={STATUS_TONE[l.status]}>{l.status}</Pill>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel testId="crm-quick-links" title="Jump to">
          <div className="grid gap-2">
            {[
              ["/app/pipeline", "Pipeline board", "GitBranch"],
              ["/app/customers", "Customer records", "Users"],
              ["/app/appointments", "Appointments", "CalendarCheck"],
              ["/app/messages", "Unified inbox", "MessagesSquare"],
              ["/app/automations", "Automation engine", "Workflow"],
            ].map(([to, label, icon]) => {
              const Icon = Icons[icon];
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={`crm-jump-${to.split("/").pop()}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-xs text-elv-dim transition-colors duration-200 hover:border-elv-green/25 hover:text-elv-text"
                >
                  <span className="flex items-center gap-2.5">
                    <Icon className="h-3.5 w-3.5 text-elv-green" />
                    {label}
                  </span>
                  <Icons.ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}
